import React from 'react';
import { Application, CartValidationTypes, ChannelTypes, Dealer, Details, IpBehavior } from '../../gql/graphql';
import { convertToCurrency } from '../../helpers/converters/converters';
import { isLongForm } from '../../helpers/converters/isLongForm';
import { selectLeaseHelper } from '../../helpers';
import { useTranslation } from 'react-i18next';

interface PageTexts {
  bodyText: (string | JSX.Element)[];
  disclosureText: string;
  buttonText: string;
}

// page text logic only
export const usePageText = (
  cartValidationErr: CartValidationTypes | null | undefined,
  orderDetails: Details,
  application: Application,
  dealer: Dealer,
): PageTexts => {
  const { t } = useTranslation();
  const leaseOption = selectLeaseHelper(application?.lease?.options); // TODO: this is a functional change, before it would use the first lease option
  const ip = application?.lease?.ipUpperBoundary;
  const ipBehavior = application?.lease?.ipBehavior;
  const initialPayment = convertToCurrency(parseFloat(ip!), 0);

  const isEmptyCartInvalid = !orderDetails?.items?.length && dealer.channelType === ChannelTypes.Ecommerce && !dealer?.isVirtualCard;

  if (!!cartValidationErr || isEmptyCartInvalid) {
    if (!!cartValidationErr) {
      return {
        bodyText: [],
        disclosureText: '',
        buttonText: t(($) => $.buttonLabels.backToCart),
      };
    } else {
      return {
        bodyText: [],
        disclosureText: '',
        buttonText: t(($) => $.buttonLabels.nextContinueShopping),
      };
    }
  }

  if (isLongForm(dealer)) {
    switch (ipBehavior) {
      case IpBehavior.AuthOnly:
        return {
          bodyText: [
            <span key="authOnlyBodyText">
              {t(($) => $.pagePreapproval.authOnlyBodyText, {
                initialPayment,
              })}
            </span>,
            <div key="authOnlyBreak" style={{ lineHeight: '0.5rem' }}>
              <br />
            </div>,
            <span key="authOnlyBodyTextContinuation">{t(($) => $.pagePreapproval.authOnlyBodyTextContinuation)}</span>,
          ],
          disclosureText: t(($) => $.pagePreapproval.disclosureText),
          buttonText: t(($) => $.pagePreapproval.finalizeWithHold, {
            initialPayment,
          }),
        };

      case IpBehavior.VariableDip:
        return {
          bodyText: [
            <span key="variableDipBodyText">{t(($) => $.pagePreapproval.variableDipBodyText)}</span>,
            <div key="variableDipBreak" style={{ lineHeight: '0.5rem' }}>
              <br />
            </div>,
            <span key="variableDipBodyTextContinuation">
              {t(($) => $.pagePreapproval.variableDipBodyTextContinuation, {
                initialPayment,
              })}
            </span>,
          ],
          disclosureText: t(($) => $.pagePreapproval.disclosureText),
          buttonText: t(($) => $.pagePreapproval.finalizeWithHold, {
            initialPayment: '$0.01',
          }),
        };

      case IpBehavior.Dip:
        return {
          bodyText: [
            <span key="dipBodyText">{t(($) => $.pagePreapproval.dipBodyText)}</span>,
            <div key="dipBreak" style={{ lineHeight: '0.5rem' }}>
              <br />
            </div>,
            <span key="dipBodyTextContinuation">
              {t(($) => $.pagePreapproval.dipBodyTextContinuation, {
                initialPayment,
              })}
            </span>,
          ],
          disclosureText: t(($) => $.pagePreapproval.disclosureText),
          buttonText: t(($) => $.pagePreapproval.finalizeWithHold, {
            initialPayment: '$0.01',
          }),
        };

      case IpBehavior.Variable:
        return {
          bodyText: [
            <span key="variableBodyText">
              {t(($) => $.pagePreapproval.variableBodyText, {
                initialPayment,
              })}
            </span>,
            <div key="variableBreak" style={{ lineHeight: '0.5rem' }}>
              <br />
            </div>,
            <span key="variableBodyTextContinuation">
              {t(($) => $.pagePreapproval.variableBodyTextContinuation, {
                initialPayment,
              })}
            </span>,
          ],
          disclosureText: t(($) => $.pagePreapproval.disclosureText),
          buttonText: t(($) => $.pagePreapproval.finalizeWithHold, {
            initialPayment,
          }),
        };

      case IpBehavior.Traditional:
        return {
          bodyText: [
            <span key="traditionalBodyText">
              {t(($) => $.pagePreapproval.traditionalBodyText, {
                initialPayment,
              })}
            </span>,
            <div key="traditionalBreak" style={{ lineHeight: '0.5rem' }}>
              <br />
            </div>,
            <span key="traditionalBodyTextContinuation">{t(($) => $.pagePreapproval.traditionalBodyTextContinuation)}</span>,
          ],
          disclosureText: t(($) => $.pagePreapproval.disclosureText),
          buttonText: t(($) => $.pagePreapproval.finalizeWithHold, {
            initialPayment,
          }),
        };
    }
  }

  // short form and default
  return {
    bodyText: [
      t(($) => $.pagePreapproval.shortFormBodyText, {
        applicationFee: convertToCurrency(leaseOption?.applicationFee ?? 0),
      }),
      <div style={{ lineHeight: '0.5rem' }}>
        <br />
      </div>,
      t(($) => $.pagePreapproval.shortFormBodyTextContinuation),
    ],
    disclosureText: t(($) => $.pagePreapproval.disclosureText),
    buttonText: t(($) => $.pagePreapproval.finalizeApprovalButton),
  };
};
